import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase.config";
import Modal from "react-modal";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
  ArcElement,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCertificate, faFile } from "@fortawesome/free-solid-svg-icons";
import md5 from "md5";

// Register the necessary Chart.js components
Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
  ArcElement
);

Modal.setAppElement("#root");

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedCertificates, setSelectedCertificates] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [eventCertModalOpen, setEventCertModalOpen] = useState(false);
  const [chartType, setChartType] = useState("bar");
  const [dataType, setDataType] = useState("departments");

  useEffect(() => {
    const fetchData = async () => {
      const usersSnapshot = await getDocs(collection(db, "users"));
      const certificatesSnapshot = await getDocs(
        collection(db, "certificates")
      );
      const eventsSnapshot = await getDocs(collection(db, "events"));

      setUsers(
        usersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
      setCertificates(
        certificatesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
      setEvents(
        eventsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
    };

    fetchData();
  }, []);

  const handleViewCertificates = (userId) => {
    const userCertificates = certificates.filter(
      (cert) => cert.userId === userId
    );
    setSelectedCertificates(userCertificates);
    setSelectedUser(users.find((user) => user.id === userId));
    setCertModalOpen(true);
  };

  const handleViewEventCertificates = (userId) => {
    const userEventCertificates = events.filter(
      (event) => event.userId === userId
    );
    setSelectedCertificates(userEventCertificates);
    setSelectedUser(users.find((user) => user.id === userId));
    setEventCertModalOpen(true);
  };

  const certificatesByCompany = certificates.reduce((acc, cert) => {
    acc[cert.company] = (acc[cert.company] || 0) + 1;
    return acc;
  }, {});

  const eventsByName = events.reduce((acc, event) => {
    acc[event.eventName] = (acc[event.eventName] || 0) + 1;
    return acc;
  }, {});

  const departments = users.reduce((acc, user) => {
    acc[user.department] = (acc[user.department] || 0) + 1;
    return acc;
  }, {});

  const clubs = users.reduce((acc, user) => {
    acc[user.club] = (acc[user.club] || 0) + 1;
    return acc;
  }, {});

  const generateChartData = () => {
    switch (dataType) {
      case "departments":
        return {
          labels: Object.keys(departments),
          datasets: [
            {
              label: "Departments",
              data: Object.values(departments),
              backgroundColor: "rgba(75, 192, 192, 0.6)",
            },
          ],
        };
      case "clubs":
        return {
          labels: Object.keys(clubs),
          datasets: [
            {
              label: "Clubs",
              data: Object.values(clubs),
              backgroundColor: "rgba(153, 102, 255, 0.6)",
            },
          ],
        };
      case "certificates":
        return {
          labels: Object.keys(certificatesByCompany),
          datasets: [
            {
              label: "Certificates by Company",
              data: Object.values(certificatesByCompany),
              backgroundColor: "rgba(75, 192, 192, 0.6)",
            },
          ],
        };
      case "events":
        return {
          labels: Object.keys(eventsByName),
          datasets: [
            {
              label: "Events by Name",
              data: Object.values(eventsByName),
              backgroundColor: "rgba(153, 102, 255, 0.6)",
            },
          ],
        };
      default:
        return {};
    }
  };

  const pieChartData = {
    labels: Object.keys(departments),
    datasets: [
      {
        label: "Departments",
        data: Object.values(departments),
        backgroundColor: [
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-gray-100 p-8">
      <h1 className="text-3xl font-bold mb-6">Admin Dashboard</h1>
      {/* Users Table */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Users</h2>
        <table className="min-w-full bg-gray-700 text-white text-sm border border-white rounded-lg">
          <thead>
            <tr>
              <th className="py-2 px-4 border-b border-white">Profile</th>
              <th className="py-2 px-4 border-b border-white">Name</th>
              <th className="py-2 px-4 border-b border-white">Email</th>
              <th className="py-2 px-4 border-b border-white">Department</th>
              <th className="py-2 px-4 border-b border-white">Club</th>
              <th className="py-2 px-4 border-b border-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((user) => (
                <tr key={user.id}>
                  <td className="py-2 px-4 border-b border-white">
                    <img
                      src={
                        user.profileImageUrl ||
                        `https://www.gravatar.com/avatar/${md5(user.email)}`
                      }
                      alt={user.name}
                      className="w-10 h-10 rounded-full"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/default-avatar.png"; // Fallback image
                      }}
                    />
                  </td>
                  <td className="py-2 px-4 border-b border-white">
                    {user.name}
                  </td>
                  <td className="py-2 px-4 border-b border-white">
                    {user.email}
                  </td>
                  <td className="py-2 px-4 border-b border-white">
                    {user.department}
                  </td>
                  <td className="py-2 px-4 border-b border-white">
                    {user.club}
                  </td>
                  <td className="py-2 px-4 border-b border-white">
                    <button
                      onClick={() => handleViewCertificates(user.id)}
                      className="bg-green-600 text-white py-1 px-2 rounded hover:bg-green-700 transition duration-300 mr-2"
                    >
                      <FontAwesomeIcon icon={faCertificate} className="mr-1" />
                      View Certificates
                    </button>
                    <button
                      onClick={() => handleViewEventCertificates(user.id)}
                      className="bg-purple-600 text-white py-1 px-2 rounded hover:bg-purple-700 transition duration-300"
                    >
                      <FontAwesomeIcon icon={faFile} className="mr-1" />
                      View Event Certificates
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Filters & Sorting */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex space-x-4">
          <select
            className="bg-gray-800 text-white py-2 px-4 rounded"
            onChange={(e) => setChartType(e.target.value)}
            value={chartType}
          >
            <option value="bar">Bar Chart</option>
            <option value="pie">Pie Chart</option>
          </select>
          <select
            className="bg-gray-800 text-white py-2 px-4 rounded"
            onChange={(e) => setDataType(e.target.value)}
            value={dataType}
          >
            <option value="departments">Departments</option>
            <option value="clubs">Clubs</option>
            <option value="certificates">Certificates</option>
            <option value="events">Events</option>
          </select>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          {dataType.charAt(0).toUpperCase() + dataType.slice(1)} Chart
        </h2>
        <div className=" h-72">
          {chartType === "bar" ? (
            <Bar
              data={generateChartData()}
              options={{ maintainAspectRatio: false }}
            />
          ) : (
            <Pie data={pieChartData} options={{ maintainAspectRatio: false }} />
          )}
        </div>
      </div>

      {/* Certificates Modal */}
      <Modal
        isOpen={certModalOpen}
        onRequestClose={() => setCertModalOpen(false)}
        className="bg-gray-900 text-white rounded-lg p-8"
      >
        <h2 className="text-2xl font-bold mb-4">
          {selectedUser?.name}'s Certificates
        </h2>
        {selectedCertificates.map((cert) => (
          <div key={cert.id} className="mb-4">
            <h3 className="text-xl">{cert.courseName}</h3>
            <p>Duration: {cert.courseDuration}</p>
            <p>Company: {cert.company}</p>
          </div>
        ))}
        <button
          onClick={() => setCertModalOpen(false)}
          className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition duration-300 mt-4"
        >
          Close
        </button>
      </Modal>

      {/* Event Certificates Modal */}
      <Modal
        isOpen={eventCertModalOpen}
        onRequestClose={() => setEventCertModalOpen(false)}
        className="bg-gray-900 text-white rounded-lg p-8"
      >
        <h2 className="text-2xl font-bold mb-4">
          {selectedUser?.name}'s Event Certificates
        </h2>
        {selectedCertificates.map((event) => (
          <div key={event.id} className="mb-4">
            <h3 className="text-xl">{event.eventName}</h3>
            <p>Description: {event.eventDescription}</p>
          </div>
        ))}
        <button
          onClick={() => setEventCertModalOpen(false)}
          className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition duration-300 mt-4"
        >
          Close
        </button>
      </Modal>
    </div>
  );
};

export default Admin;
